import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { ProductionBatchDrawer } from "../components/production/ProductionBatchDrawer";
import { StockProductionPicker } from "../components/production/StockProductionPicker";
import { StockBatchLinkDrawer } from "../components/production/StockBatchLinkDrawer";
import { BatchConsumptionDrawer } from "../components/production/BatchConsumptionDrawer";
import { WorkOrderDetailDrawer } from "../components/production/WorkOrderDetailDrawer";
import { FaultReportDrawer } from "../components/production/FaultReportDrawer";
import { LineManagementDrawer } from "../components/production/LineManagementDrawer";
import { PersonnelAttendanceDrawer } from "../components/production/PersonnelAttendanceDrawer";
import { Icon } from "../components/ui/Icon";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { printBatchLabel } from "../lib/batchLabel";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard, Chips, Sheet } from "../components/mobile/primitives";
import { SearchSelect, type SSOption } from "../components/ui/SearchSelect";
import "./Items.css";

type ItemKind = "stock" | "semi" | "finished";

interface OrderLine {
  id: string;
  itemKind: ItemKind;
  itemId: string;
  itemCode: string;
  itemName: string;
  qty: number;
  uom: string;
  uomId: string;
  producedQty: number;
}
interface InProdOrder {
  id: string;
  code: string;
  customerName: string;
  createdAt: string;
  requestedDate: string | null;
  /** Etkin teslim tarihi (söz verilen > istenen) — uçta TEK yerde türetilir. */
  teslimTarihi: string | null;
  /** true = söz verilen tarih yok, istenen tarihe düşüldü → işaretlenir. */
  teslimTahmini: boolean;
  awaitingQc: boolean; // tam üretildi ama partiler QC onayı bekliyor → sevkiyata hazır değil
  lines: OrderLine[];
}

interface BatchRow {
  id: string;
  code: string;
  lotId: string | null;
  orderCode: string;
  customerName: string;
  itemCode: string;
  itemName: string;
  qty: number;
  uom: string;
  locationName: string;
  locationCode: string;
  lotCode: string | null;
  lotStatus: string | null;
  lotQtyOnHand: number | null;
  producedAt: string;
  parentBatchId: string | null;
  parentCode: string | null;
  shiftName: string | null;
  params: Array<{ paramId: string; value: string }>;
}

interface CenterParam {
  id: string;
  label: string;
  dataType: "number" | "text" | "select" | "boolean" | "date";
  unit: string | null;
}

const LOT_BADGE: Record<string, { cls: string; label: string }> = {
  pending_qc: { cls: "b-warn", label: "Kalite Kontrol Bekleniyor" },
  released: { cls: "b-success", label: "Serbest" },
  on_hold: { cls: "b-warn", label: "Beklemede" },
  expired: { cls: "b-danger", label: "SKT Geçti" },
  rejected: { cls: "b-danger", label: "Red" },
  consumed: { cls: "", label: "Tükendi" },
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: "8px 14px", fontSize: 13, fontWeight: active ? 600 : 500,
  color: active ? "var(--primary)" : "var(--neutral-600)",
  background: "transparent", border: 0,
  borderBottom: active ? "2px solid var(--primary)" : "2px solid transparent",
  cursor: "pointer", marginBottom: -1, whiteSpace: "nowrap",
});

type CenterMetaLite = {
  dailyCapacity: string | number | null;
  capacityUnit: string | null;
  shiftsPerDay: number | null;
  hoursPerShift: string | number | null;
  performancePct: string | number | null;
  maxConcurrentWo: number | null;
  allowStockProduction?: boolean | null;
} | null;

type CenterLite = { id: string; code: string; name: string; type: string; isActive: boolean; parentId: string | null; productionCenter: CenterMetaLite };
type QueueRow = { id: string; code: string; producedCode: string; producedName: string; producedKind: "semi" | "finished"; producedId: string; plannedQty: number; producedQty: number; status: string; subWoCount: number; sourceOrderId: string | null; sourceOrderCode: string | null; customerName: string | null; batchSize: number | null; batchUom: string | null };
type TodayStats = { count: number; byUom: Array<{ qty: number; uom: string }>; byShift?: Array<{ name: string; count: number }> } | null;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: "var(--neutral-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div className="mono" style={{ fontSize: 15, fontWeight: 600, color: "var(--neutral-800)" }}>{value}</div>
    </div>
  );
}

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}
function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
function fmtDateTime(d: string): string {
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(d));
}

interface BatchTarget {
  orderId?: string | null;
  orderCode?: string;
  woId?: string;
  woCode?: string;
  centerId?: string | null;
  itemKind: "semi" | "finished";
  itemId: string;
  itemLabel: string;
  uom: string;
  orderedQty?: number;
  producedQty?: number;
}

export function ProductionPage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState<BatchTarget | null>(null);
  const [stockPickOpen, setStockPickOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState<{ woId: string; woCode: string; itemLabel: string; plannedQty: number; producedQty: number } | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = useCallback((t: Omit<ToastItem, "id">) => {
    const id = String(++toastSeq.current);
    setToasts((prev) => [...prev.slice(-4), { ...t, id }]); // aynı anda en çok 5 toast görünür
  }, []);
  const dismissToast = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const [page, setPage] = useState(1);
  const [batchSearch, setBatchSearch] = useState(""); // parti tablosu hızlı arama (satır-geneli)
  const [batchQDebounced, setBatchQDebounced] = useState("");
  useEffect(() => { const t = setTimeout(() => setBatchQDebounced(batchSearch.trim()), 250); return () => clearTimeout(t); }, [batchSearch]);
  // Parti tablosu DETAYLI FİLTRE (ürün + tarih aralığı + vardiya + kullanıcı) — hepsi AND, hızlı arama ile birlikte.
  const [filterOpen, setFilterOpen] = useState(false);
  const [fProduct, setFProduct] = useState(""); // "kind:id"
  const [fSince, setFSince] = useState("");
  const [fUntil, setFUntil] = useState("");
  const [fShift, setFShift] = useState("");
  const [fUser, setFUser] = useState("");
  const filterCount = (fProduct ? 1 : 0) + (fSince ? 1 : 0) + (fUntil ? 1 : 0) + (fShift ? 1 : 0) + (fUser ? 1 : 0);
  const setFilter = (fn: () => void) => { fn(); setPage(1); };
  const clearFilters = () => { setFProduct(""); setFSince(""); setFUntil(""); setFShift(""); setFUser(""); setPage(1); };
  const fLabel: CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--neutral-500)", textTransform: "uppercase", letterSpacing: "0.04em" };
  const [selectedCenter, setSelectedCenter] = useState<string | null>(null); // null = Tümü
  const [faultReportOpen, setFaultReportOpen] = useState(false);
  const [lineManageOpen, setLineManageOpen] = useState(false);
  const [delBatch, setDelBatch] = useState<{ id: string; code: string } | null>(null);
  const [traceTarget, setTraceTarget] = useState<{ lotId: string | null; code: string } | null>(null);
  const pageSize = 20;

  const delMut = trpc.productionBatch.delete.useMutation();
  function runDeleteBatch() {
    if (!delBatch) return;
    delMut.mutate({ id: delBatch.id }, {
      onSuccess: (res) => {
        setDelBatch(null); delMut.reset();
        qc.invalidateQueries();
        pushToast({ tone: "success", message: `${res.code} silindi · ${res.reversed} stok hareketi geri alındı.` });
      },
      onError: (err) => { pushToast({ tone: "danger", title: "Parti silinemedi", message: err.message }); },
    });
  }
  const [detailWoId, setDetailWoId] = useState<string | null>(null); // iş emri detay/iptal drawer'ı
  const [attendanceOpen, setAttendanceOpen] = useState(false); // personel devam beyanı çekmecesi

  const centersQ = trpc.location.list.useQuery(undefined, { staleTime: 60_000 });
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const assignedIds = (meQ.data?.user as { assignedLocationIds?: string[] } | undefined)?.assignedLocationIds ?? [];
  const allLocs = useMemo(
    () => (centersQ.data ?? []) as Array<{ id: string; code: string; name: string; type: string; isActive: boolean; parentId: string | null; productionCenter: CenterMetaLite }>,
    [centersQ.data],
  );
  // Merkez-atamalı kullanıcı: erişilebilir merkez id'leri (atanan merkez + atanan hattın kapsayan merkezi). Atama yoksa null = tümü.
  const accessibleCenterIds = useMemo(() => {
    if (assignedIds.length === 0) return null;
    const byId = new Map(allLocs.map((l) => [l.id, l]));
    const centerOf = (id: string): string | null => {
      let cur = byId.get(id); let g = 0;
      while (cur && cur.type !== "uretim_merkezi" && g++ < 20) cur = cur.parentId ? byId.get(cur.parentId) : undefined;
      return cur?.type === "uretim_merkezi" ? cur.id : null;
    };
    return new Set(assignedIds.map(centerOf).filter((x): x is string => !!x));
  }, [assignedIds, allLocs]);
  const hasCenterScope = accessibleCenterIds !== null;
  const centers = useMemo(
    () => allLocs.filter((l) => l.type === "uretim_merkezi" && l.isActive && (!accessibleCenterIds || accessibleCenterIds.has(l.id))),
    [allLocs, accessibleCenterIds],
  );
  const selectedCenterObj = selectedCenter ? centers.find((c) => c.id === selectedCenter) : undefined;
  const centerName = selectedCenterObj?.name ?? "";
  const selectedMeta = selectedCenterObj?.productionCenter ?? null;
  // Seçili merkez pasifleşir/silinirse (başka sekmede) sekmeyi düşür.
  // Merkez-atamalı kullanıcıda "Tümü" yok → ilk atanmış merkeze düş; değilse "Tümü"ye.
  useEffect(() => {
    if (!centersQ.data) return;
    if (selectedCenter !== null && !centers.some((c) => c.id === selectedCenter)) {
      setSelectedCenter(hasCenterScope ? (centers[0]?.id ?? null) : null); setPage(1);
    } else if (selectedCenter === null && hasCenterScope && centers[0]) {
      setSelectedCenter(centers[0].id); setPage(1);
    }
  }, [centers, selectedCenter, centersQ.data, hasCenterScope]);

  const ordersQ = trpc.productionBatch.ordersInProduction.useQuery(undefined, { staleTime: 10_000, enabled: selectedCenter === null });
  const woQueueQ = trpc.workOrder.list.useQuery(
    { page: 1, pageSize: 100, productionCenterId: selectedCenter ?? undefined, active: true, restrictToActiveOrders: true },
    { enabled: !!selectedCenter, staleTime: 10_000 },
  );
  const batchInput = useMemo(() => {
    const [pk, pid] = fProduct ? fProduct.split(":") : [undefined, undefined];
    const dayStart = (ymd: string) => { const [y, m, d] = ymd.split("-").map(Number); return new Date(y!, m! - 1, d!, 0, 0, 0, 0).toISOString(); };
    const dayEndExcl = (ymd: string) => { const [y, m, d] = ymd.split("-").map(Number); return new Date(y!, m! - 1, d! + 1, 0, 0, 0, 0).toISOString(); };
    return {
      page, pageSize, centerId: selectedCenter ?? undefined, q: batchQDebounced || undefined,
      itemKind: pk as "stock" | "semi" | "finished" | undefined, itemId: pid || undefined,
      shiftId: fShift || undefined, createdBy: fUser || undefined,
      since: fSince ? dayStart(fSince) : undefined, until: fUntil ? dayEndExcl(fUntil) : undefined,
    };
  }, [page, selectedCenter, batchQDebounced, fProduct, fShift, fUser, fSince, fUntil]);
  const batchesQ = trpc.productionBatch.list.useQuery(batchInput, { staleTime: 10_000 });
  // Detaylı filtre seçenekleri (merkezde üretimi olan ürünler + üreten kullanıcılar) + vardiyalar.
  const filterOptQ = trpc.productionBatch.filterOptions.useQuery({ centerId: selectedCenter ?? undefined }, { staleTime: 30_000 });
  const shiftsQ = trpc.settings.workSchedule.get.useQuery(undefined, { staleTime: 5 * 60_000 });
  // SearchSelect (aranabilir, stilize) seçenekleri — referans filtre deseni (native <select> değil).
  const productOptions = useMemo<SSOption[]>(() => (filterOptQ.data?.products ?? []).map((p) => ({ value: `${p.itemKind}:${p.itemId}`, label: p.name, tail: p.code })), [filterOptQ.data]);
  const shiftOptions = useMemo<SSOption[]>(() => (shiftsQ.data?.shifts ?? []).filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name, desc: `${s.startTime}–${s.endTime}` })), [shiftsQ.data]);
  const userOptions = useMemo<SSOption[]>(() => (filterOptQ.data?.users ?? []).map((u) => ({ value: u.id, label: u.name })), [filterOptQ.data]);
  // Firma bilgileri (parti etiketi başlığındaki logo + ünvan için).
  const profQ = trpc.settings.companyProfile.get.useQuery(undefined, { staleTime: 5 * 60_000 });
  // Bugün (yerel gün başı) merkezde üretilen toplam parti — kapasite özet bandındaki metrik.
  const startOfTodayISO = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); }, []);
  const todayStatsQ = trpc.productionBatch.todayStats.useQuery(
    { centerId: selectedCenter ?? "", since: startOfTodayISO },
    { enabled: selectedCenter !== null, staleTime: 30_000 },
  );
  // Seçili merkezin aktif parametreleri → parti tablosunda satır içi kolonlar.
  const centerParamsQ = trpc.productionCenterParam.list.useQuery(
    { centerId: selectedCenter ?? "", activeOnly: true },
    { enabled: !!selectedCenter, staleTime: 30_000 },
  );
  const centerParams = (selectedCenter ? (centerParamsQ.data ?? []) : []) as CenterParam[];

  const orders = (ordersQ.data ?? []) as unknown as InProdOrder[];
  const queue = (woQueueQ.data?.rows ?? []) as QueueRow[];
  const batches = (batchesQ.data?.rows ?? []) as unknown as BatchRow[];
  const batchTotal = batchesQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(batchTotal / pageSize));

  function selectTab(id: string | null) { setSelectedCenter(id); setPage(1); }

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openBatch(o: InProdOrder, l: OrderLine) {
    if (l.itemKind === "stock") return; // stok kalemi üretilmez
    setTarget({
      orderId: o.id, orderCode: o.code,
      itemKind: l.itemKind, itemId: l.itemId, itemLabel: l.itemName,
      uom: l.uom, orderedQty: l.qty, producedQty: l.producedQty,
    });
  }

  // Merkez kuyruğundaki bir iş emri için parti üretim kaydı (woId + merkez hattı).
  function openWoBatch(w: (typeof queue)[number]) {
    setTarget({
      woId: w.id, woCode: w.code, centerId: selectedCenter,
      orderId: w.sourceOrderId,
      itemKind: w.producedKind, itemId: w.producedId, itemLabel: w.producedName,
      uom: "", orderedQty: w.plannedQty, producedQty: w.producedQty,
    });
  }

  function handlePrint(b: BatchRow) {
    const prof = profQ.data;
    const ok = printBatchLabel({
      batchCode: b.code,
      itemName: b.itemName,
      itemCode: b.itemCode,
      customerName: b.customerName,
      qty: b.qty,
      uom: b.uom,
      lotCode: b.lotCode,
      locationName: b.locationName,
      producedAt: b.producedAt,
      companyName: prof?.name ?? null,
      companyLogo: prof?.logoUrl ? new URL(prof.logoUrl, window.location.origin).href : null,
    });
    if (!ok) pushToast({ tone: "warning", title: "Yazdırılamadı", message: "Tarayıcı açılır pencereyi (popup) engelledi. İzin verip tekrar deneyin." });
  }

  // Günlük üretim raporunu yeni sekmede aç (A4). Masaüstü + mobil ortak.
  function openReport() {
    const s = new Date(); s.setHours(0, 0, 0, 0);
    const u = new Date(s); u.setDate(u.getDate() + 1);
    const params = new URLSearchParams({ since: s.toISOString(), until: u.toISOString() });
    if (selectedCenter) params.set("center", selectedCenter);
    window.open(`/print/uretim-raporu?${params.toString()}`, "_blank", "noopener");
  }
  const anyFetching = ordersQ.isFetching || woQueueQ.isFetching || batchesQ.isFetching;
  function refreshAll() { ordersQ.refetch(); woQueueQ.refetch(); batchesQ.refetch(); }

  // ═══ Aksiyon drawer/panel'leri — masaüstü ve mobilde ORTAK (mevcut, ≤767px'te tam ekran) ═══
  const drawers = (
    <>
      {target ? (
        <ProductionBatchDrawer
          open={!!target}
          orderId={target.orderId}
          orderCode={target.orderCode}
          woId={target.woId}
          woCode={target.woCode}
          centerId={target.centerId}
          itemKind={target.itemKind}
          itemId={target.itemId}
          itemLabel={target.itemLabel}
          uom={target.uom}
          orderedQty={target.orderedQty}
          producedQty={target.producedQty}
          onClose={() => setTarget(null)}
          onCreated={(code, warnings, count, produced, orderReady, byproducts) => {
            const c = count ?? 1;
            const lines: string[] = [];
            if (byproducts && byproducts.length) {
              lines.push(byproducts.length <= 3
                ? `Yan ürün: ${byproducts.map((b) => `${b.code} (${b.lotCode})`).join(", ")}`
                : `${byproducts.length} yan ürün partisi üretildi`);
            }
            if (produced && produced.length) {
              lines.push(produced.length <= 4 ? `Otomatik üretilen: ${produced.join(", ")}` : `${produced.length} yarı mamul otomatik üretildi`);
            }
            if (warnings && warnings.length) lines.push(`${warnings.length} stok uyarısı (eksik stoktan düşüldü)`);
            if (orderReady) lines.push("Sipariş tamamlandı → sevkiyata hazır.");
            pushToast({
              tone: warnings && warnings.length ? "warning" : "success",
              title: c > 1 ? `${c} parti üretildi · ${code}` : `Parti üretildi · ${code}`,
              lines,
            });
            const oid = target.orderId; if (oid) setExpanded((p) => new Set(p).add(oid));
          }}
        />
      ) : null}

      <StockProductionPicker
        open={stockPickOpen}
        centerId={selectedCenter}
        centerName={centerName}
        onClose={() => setStockPickOpen(false)}
        onPick={(prod) => {
          setStockPickOpen(false);
          setTarget({ centerId: selectedCenter, orderId: null, itemKind: prod.itemKind, itemId: prod.itemId, itemLabel: prod.itemLabel, uom: prod.uom });
        }}
      />

      <StockBatchLinkDrawer
        open={!!linkTarget}
        woId={linkTarget?.woId ?? null}
        woCode={linkTarget?.woCode}
        itemLabel={linkTarget?.itemLabel}
        plannedQty={linkTarget?.plannedQty}
        producedQty={linkTarget?.producedQty}
        onClose={() => setLinkTarget(null)}
        onLinked={(res) => {
          setLinkTarget(null);
          pushToast({
            tone: "success",
            message: res.completed
              ? `${res.linked} parti ${res.woCode} iş emrine bağlandı · plan tamamlandı, iş emri kapatıldı.`
              : `${res.linked} parti ${res.woCode} iş emrine bağlandı · üretim ilerlemesine eklendi.`,
          });
        }}
      />

      <BatchConsumptionDrawer
        open={!!traceTarget}
        lotId={traceTarget?.lotId ?? null}
        batchCode={traceTarget?.code}
        onClose={() => setTraceTarget(null)}
      />

      <WorkOrderDetailDrawer
        open={!!detailWoId}
        woId={detailWoId}
        onClose={() => setDetailWoId(null)}
      />

      <ConfirmDialog
        open={!!delBatch}
        title="Partiyi sil"
        message={
          <>
            <b>{delBatch?.code}</b> partisini silmek üzeresiniz. Bu partinin <b>çıktı lotu geri alınır</b> ve
            reçete bileşenlerinden yapılan <b>stok düşümleri iade edilir</b> (çift yönlü). Çıktı lotu başka bir
            harekette kullanıldıysa silme engellenir.
          </>
        }
        tone="danger"
        confirmLabel="Partiyi Sil"
        cancelLabel="Vazgeç"
        busy={delMut.isPending}
        onConfirm={runDeleteBatch}
        onCancel={() => { setDelBatch(null); delMut.reset(); }}
      />

      <FaultReportDrawer
        open={faultReportOpen}
        centerId={selectedCenter ?? ""}
        centerName={centerName}
        onClose={() => setFaultReportOpen(false)}
      />

      <LineManagementDrawer
        open={lineManageOpen}
        centerId={selectedCenter ?? ""}
        centerName={centerName}
        onClose={() => setLineManageOpen(false)}
      />

      <PersonnelAttendanceDrawer open={attendanceOpen} onClose={() => setAttendanceOpen(false)} defaultCenterId={selectedCenter} centers={centers.map((c) => ({ id: c.id, name: c.name }))} />

      {/* Parti detaylı filtre — MOBİL (Sheet). Masaüstünde inline bar kullanılır (yukarıda).
          KOŞULLU MOUNT ŞART: `.m-sheet` position:absolute'tur ve `.m-app` kabuğu dışında
          sayfanın en altına oturup sayfayı uzatıyor (kapalıyken bile görünür kalıyordu).
          `open={false}` vermek yetmez — element yine de düzende yer kaplar. */}
      {isMobile ? (
      <Sheet open={filterOpen} title="Parti Filtresi" onClose={() => setFilterOpen(false)}
        footer={<>
          <button type="button" className="btn-md secondary" onClick={clearFilters}>Temizle</button>
          <button type="button" className="btn-md primary" onClick={() => setFilterOpen(false)}>Uygula</button>
        </>}>
        <div className="field">
          <label className="field-label">Ürün</label>
          <SearchSelect value={fProduct || null} options={productOptions} onChange={(v) => setFilter(() => setFProduct(v ?? ""))} placeholder="Tüm ürünler" searchPlaceholder="Ürün adı / kodu ara…" />
        </div>
        <div className="field">
          <label className="field-label">Tarih Aralığı</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" className="form-input" style={{ flex: 1 }} value={fSince} max={fUntil || undefined} onChange={(e) => setFilter(() => setFSince(e.target.value))} aria-label="Başlangıç" />
            <span style={{ color: "var(--neutral-400)" }}>–</span>
            <input type="date" className="form-input" style={{ flex: 1 }} value={fUntil} min={fSince || undefined} onChange={(e) => setFilter(() => setFUntil(e.target.value))} aria-label="Bitiş" />
          </div>
        </div>
        <div className="field">
          <label className="field-label">Vardiya</label>
          <SearchSelect value={fShift || null} options={shiftOptions} onChange={(v) => setFilter(() => setFShift(v ?? ""))} placeholder="Tüm vardiyalar" searchPlaceholder="Vardiya ara…" />
        </div>
        <div className="field">
          <label className="field-label">Kullanıcı</label>
          <SearchSelect value={fUser || null} options={userOptions} onChange={(v) => setFilter(() => setFUser(v ?? ""))} placeholder="Tüm kullanıcılar" searchPlaceholder="Kullanıcı ara…" />
        </div>
      </Sheet>
      ) : null}

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </>
  );

  // ═══ MOBİL GÖRÜNÜM (≤767px) — GENTAŞ Saha: chips + KPI + kart listeleri ═══
  if (isMobile) {
    return (
      <>
        <MobileProduction
          centers={centers}
          selectedCenter={selectedCenter}
          selectTab={selectTab}
          hasCenterScope={hasCenterScope}
          selectedMeta={selectedMeta}
          centerName={centerName}
          todayStats={todayStatsQ.data ?? null}
          orders={orders}
          ordersLoading={ordersQ.isLoading}
          ordersError={ordersQ.isError ? ordersQ.error?.message ?? "Veri yüklenemedi" : null}
          expanded={expanded}
          toggle={toggle}
          openBatch={openBatch}
          queue={queue}
          queueLoading={woQueueQ.isLoading}
          openWoBatch={openWoBatch}
          onWoDetail={setDetailWoId}
          onStockLink={(w) => setLinkTarget({ woId: w.id, woCode: w.code, itemLabel: w.producedName, plannedQty: w.plannedQty, producedQty: w.producedQty })}
          onStockProduce={() => setStockPickOpen(true)}
          allowStock={!!selectedMeta?.allowStockProduction}
          batches={batches}
          batchesLoading={batchesQ.isLoading}
          batchesError={batchesQ.isError ? batchesQ.error?.message ?? "Veri yüklenemedi" : null}
          batchTotal={batchTotal}
          page={page}
          totalPages={totalPages}
          setPage={setPage}
          onPrint={handlePrint}
          onTrace={(b) => setTraceTarget({ lotId: b.lotId, code: b.code })}
          onDelete={(b) => { delMut.reset(); setDelBatch({ id: b.id, code: b.code }); }}
          onFault={() => setFaultReportOpen(true)}
          onLine={() => setLineManageOpen(true)}
          onAttendance={() => setAttendanceOpen(true)}
          batchSearch={batchSearch}
          onBatchSearch={(v) => { setBatchSearch(v); setPage(1); }}
          onFilter={() => setFilterOpen(true)}
          filterCount={filterCount}
          openReport={openReport}
          refreshAll={refreshAll}
          anyFetching={anyFetching}
        />
        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Üretim</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Üretim</h1>
          <p className="sub">Üretim merkezi başına iş emri kuyruğu · parti üretim kaydı · lot takibi</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn-md primary" title="Günlük üretim raporunu yeni sekmede aç (A4, yazdırılabilir)"
            onClick={() => {
              const s = new Date(); s.setHours(0, 0, 0, 0);
              const u = new Date(s); u.setDate(u.getDate() + 1);
              const params = new URLSearchParams({ since: s.toISOString(), until: u.toISOString() });
              if (selectedCenter) params.set("center", selectedCenter);
              window.open(`/print/uretim-raporu?${params.toString()}`, "_blank", "noopener");
            }}>
            <Icon name="fileText" size={14} />Üretim Raporu
          </button>
          <button type="button" className="btn-md secondary" title="Vardiya başına pozisyon kişi sayısı beyanı" onClick={() => setAttendanceOpen(true)}>
            <Icon name="users" size={14} />Devam Beyanı
          </button>
          <button type="button" className="btn-md secondary" onClick={() => { ordersQ.refetch(); woQueueQ.refetch(); batchesQ.refetch(); }} disabled={ordersQ.isFetching || woQueueQ.isFetching || batchesQ.isFetching}>
            <Icon name="refresh" size={14} />{ordersQ.isFetching || woQueueQ.isFetching || batchesQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>
      </div>

      {/* Üretim merkezi sekmeleri (çoklanabilir) */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap", borderBottom: "1px solid var(--neutral-200)" }}>
        {!hasCenterScope ? <button type="button" style={tabStyle(selectedCenter === null)} onClick={() => selectTab(null)}>Tümü</button> : null}
        {centers.map((c) => (
          <button key={c.id} type="button" style={tabStyle(selectedCenter === c.id)} onClick={() => selectTab(c.id)} title={c.code}>{c.name}</button>
        ))}
        {centers.length === 0 ? (
          <span style={{ padding: "8px 10px", fontSize: 12, color: "var(--neutral-400)" }}>Üretim merkezi tanımlı değil — Lokasyonlar sayfasından ekleyebilirsiniz.</span>
        ) : null}
      </div>

      {/* Seçili merkezin kapasite/performans özeti */}
      {selectedCenter !== null ? (
        <div className="d-card" style={{ marginBottom: 16, padding: "12px 16px", display: "flex", gap: 28, flexWrap: "wrap", alignItems: "center" }}>
          <Stat label="Günlük Kapasite" value={selectedMeta?.dailyCapacity != null ? `${fmtQty(Number(selectedMeta.dailyCapacity))}${selectedMeta.capacityUnit ? ` ${selectedMeta.capacityUnit}` : ""}` : "—"} />
          <Stat label="Vardiya" value={selectedMeta?.shiftsPerDay != null ? `${selectedMeta.shiftsPerDay} × ${selectedMeta.hoursPerShift != null ? Number(selectedMeta.hoursPerShift) : "?"} sa` : "—"} />
          <Stat label="Performans (OEE)" value={selectedMeta?.performancePct != null ? `%${fmtQty(Number(selectedMeta.performancePct))}` : "—"} />
          <Stat label="Eş Zamanlı İş Emri" value={selectedMeta?.maxConcurrentWo != null ? String(selectedMeta.maxConcurrentWo) : "—"} />
          <Stat
            label="Bugün Üretilen"
            value={(() => {
              const d = todayStatsQ.data;
              if (!d) return "—";
              if (d.count === 0) return "0 parti";
              const parts = d.byUom.map((u) => `${fmtQty(u.qty)} ${u.uom}`);
              // Vardiya kırılımı: en az bir parti gerçek vardiyaya bağlıysa göster (V1: 3 · V2: 2).
              const shifts = (d.byShift ?? []).filter((s) => s.name !== "Vardiya dışı");
              const shiftStr = shifts.length ? ` — ${(d.byShift ?? []).map((s) => `${s.name}: ${s.count}`).join(" · ")}` : "";
              return `${d.count} parti${parts.length ? " · " + parts.join(" · ") : ""}${shiftStr}`;
            })()}
          />
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn-md danger-outline"
            style={{ height: 32, padding: "0 12px", fontSize: 12.5 }}
            title="Bu hatta/merkezde yaşanan arızayı bildir"
            onClick={() => setFaultReportOpen(true)}
          >
            <Icon name="alertTriangle" size={14} />Arıza Bildirimi
          </button>
          <button
            type="button"
            className="btn-md secondary"
            style={{ height: 32, padding: "0 12px", fontSize: 12.5 }}
            title="Üretim hatlarının anlık durumunu (Çalışıyor/Kapalı/Arızalı/Bakımda) yönet"
            onClick={() => setLineManageOpen(true)}
          >
            <Icon name="activity" size={14} />Hat Yönetimi
          </button>
        </div>
      ) : null}

      {/* Bildirimler artık sağ-üst toast yığınında (ToastStack) gösterilir. */}

      {/* ÜST: Tümü → üretimdeki siparişler · merkez → o merkezin iş emri kuyruğu */}
      {selectedCenter === null ? (
      <div className="dt2-wrap" style={{ marginBottom: 20 }}>
        <div className="dt2-toolbar">
          <b style={{ fontSize: 13 }}>Üretimdeki Siparişler</b>
          <span className="sec" style={{ color: "var(--neutral-500)", fontSize: 12 }}>{orders.length} sipariş</span>
          <span className="grow" />
        </div>

        <div className="dt2-scroll">
          {ordersQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : ordersQ.isError ? (
            <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{ordersQ.error?.message}</p></div>
          ) : orders.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Üretimde sipariş yok</p>
              <p className="sub">Siparişler sayfasından bir siparişin durumunu "Üretimde" yapın.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 10 }}>
              {orders.map((o) => {
                const open = expanded.has(o.id);
                const totalLines = o.lines.length;
                const doneLines = o.lines.filter((l) => l.producedQty >= l.qty && l.qty > 0).length;
                const totalOrdered = o.lines.reduce((s, l) => s + l.qty, 0);
                const totalProduced = o.lines.reduce((s, l) => s + l.producedQty, 0);
                const totalRemaining = o.lines.reduce((s, l) => s + Math.max(0, l.qty - l.producedQty), 0);
                const units = new Set(o.lines.map((l) => l.uom));
                const headUom = units.size === 1 ? [...units][0] : "";
                const allDone = totalOrdered > 0 && totalRemaining <= 0;
                return (
                  <div key={o.id} className="d-card" style={{ padding: 0, overflow: "hidden" }}>
                    <button
                      type="button"
                      onClick={() => toggle(o.id)}
                      aria-expanded={open}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", background: "transparent", border: 0, cursor: "pointer", textAlign: "left" }}
                    >
                      <Icon name={open ? "chevDown" : "chevRight"} size={16} />
                      <span className="cell-id">{o.code}</span>
                      <span style={{ fontWeight: 500, fontSize: 13 }}>{o.customerName}</span>
                      <span style={{ flex: 1 }} />
                      <span style={{ display: "flex", gap: 14, fontSize: 11.5, alignItems: "baseline" }}>
                        <span><span style={{ color: "var(--neutral-400)" }}>Sipariş </span><b className="mono" style={{ color: "var(--neutral-700)" }}>{fmtQty(totalOrdered)}{headUom ? ` ${headUom}` : ""}</b></span>
                        <span><span style={{ color: "var(--neutral-400)" }}>Üretilen </span><b className="mono" style={{ color: totalProduced > 0 ? "var(--success-text)" : "var(--neutral-400)" }}>{fmtQty(totalProduced)}</b></span>
                        <span><span style={{ color: "var(--neutral-400)" }}>Kalan </span><b className="mono" style={{ color: allDone ? "var(--success-text)" : "var(--warning-text)" }}>{fmtQty(totalRemaining)}</b></span>
                      </span>
                      <span style={{ width: 1, alignSelf: "stretch", margin: "2px 0", background: "var(--neutral-200)" }} />
                      <span style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>
                        Teslim: <span className="mono">{fmtDate(o.teslimTarihi)}</span>{o.teslimTahmini ? <span title="Söz verilen teslim tarihi girilmemiş — istenen tarih gösteriliyor"> *</span> : null}
                      </span>
                      {o.awaitingQc ? (
                        <span className="badge-pill b-warn" style={{ display: "inline-flex", alignItems: "center", gap: 3 }} title="Tüm partiler üretildi; sevkiyata hazır olması için QC onayı bekleniyor.">
                          <Icon name="clock" size={11} />QC bekliyor
                        </span>
                      ) : null}
                      <span className={`badge-pill ${doneLines === totalLines && totalLines > 0 ? "b-success" : "b-warn"}`}>
                        {doneLines}/{totalLines} kalem
                      </span>
                    </button>

                    {open ? (
                      <table className="dt2" style={{ borderTop: "1px solid var(--neutral-200)" }}>
                        <thead>
                          <tr>
                            <th>Ürün</th>
                            <th className="num" style={{ width: 120 }}>Sipariş</th>
                            <th className="num" style={{ width: 120 }}>Üretilen</th>
                            <th className="num" style={{ width: 120 }}>Kalan</th>
                            <th style={{ width: 116 }}>İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {o.lines.map((l) => {
                            const complete = l.qty > 0 && l.producedQty >= l.qty;
                            const remaining = Math.max(0, l.qty - l.producedQty);
                            const isStock = l.itemKind === "stock";
                            return (
                              <tr key={l.id}>
                                <td>
                                  {l.itemName}
                                  <span className="sec mono" style={{ fontSize: 10.5 }}>{l.itemCode}</span>
                                </td>
                                <td className="cell-num">{fmtQty(l.qty)} <span style={{ color: "var(--neutral-400)" }}>{l.uom}</span></td>
                                <td className="cell-num">
                                  <span style={{ color: complete ? "var(--success-text)" : l.producedQty > 0 ? "var(--warning-text)" : "var(--neutral-400)" }}>
                                    {fmtQty(l.producedQty)}
                                  </span>
                                </td>
                                <td className="cell-num">
                                  {complete ? (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, justifyContent: "flex-end", color: "var(--success-text)", fontWeight: 600 }}>
                                      <Icon name="check" size={13} />0
                                    </span>
                                  ) : (
                                    <span style={{ fontWeight: 600, color: "var(--neutral-700)" }}>{fmtQty(remaining)} <span style={{ color: "var(--neutral-400)" }}>{l.uom}</span></span>
                                  )}
                                </td>
                                <td>
                                  {isStock ? (
                                    <span style={{ fontSize: 11, color: "var(--neutral-400)" }}>stok kalemi</span>
                                  ) : (
                                    <button type="button" className="btn-md primary" style={{ height: 30, padding: "0 11px", fontSize: 12 }} onClick={() => openBatch(o, l)}>
                                      <Icon name="plus" size={13} />Üretim
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      ) : (
        <div className="dt2-wrap" style={{ marginBottom: 20 }}>
          <div className="dt2-toolbar">
            <b style={{ fontSize: 13 }}>İş Emri Kuyruğu — {centerName}</b>
            <span className="sec" style={{ color: "var(--neutral-500)", fontSize: 12 }}>{queue.length} açık iş emri</span>
            <span className="grow" />
            {selectedMeta?.allowStockProduction ? (
              <button type="button" className="btn-md primary" onClick={() => setStockPickOpen(true)} title="Sipariş olmadan stoğa üretim">
                <Icon name="plus" size={14} />Stoğa Üretim
              </button>
            ) : null}
          </div>
          <div className="dt2-scroll">
            {woQueueQ.isLoading ? (
              <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
            ) : queue.length === 0 ? (
              <div className="empty-state">
                <p className="ttl">Bu merkezde açık iş emri yok</p>
                <p className="sub">Siparişler sayfasında "Üretime Al" ile iş emirleri ürünlerin reçetesindeki merkeze dağıtılır.</p>
              </div>
            ) : (
              <table className="dt2">
                <thead>
                  <tr>
                    <th style={{ width: 168 }}>İş Emri</th>
                    <th>Ürün</th>
                    <th style={{ width: 170 }}>Müşteri</th>
                    <th className="num" style={{ width: 110 }}>Planlanan</th>
                    <th className="num" style={{ width: 110 }}>Üretilen</th>
                    <th className="num" style={{ width: 110 }}>Kalan</th>
                    <th className="num" style={{ width: 108 }} title="Kalan miktar ÷ varsayılan parti boyu (aktif reçete) = teorik kalan parti sayısı">Kalan Parti</th>
                    <th style={{ width: 232 }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map((w) => {
                    const remaining = Math.max(0, w.plannedQty - w.producedQty);
                    const complete = w.plannedQty > 0 && w.producedQty >= w.plannedQty;
                    return (
                      <tr key={w.id}>
                        <td><button type="button" className="cell-id" onClick={() => setDetailWoId(w.id)} title="İş emri detayı / iptal" style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer", color: "var(--primary)", textDecoration: "underline", textUnderlineOffset: 2, font: "inherit" }}>{w.code}</button>{w.subWoCount > 0 ? <span className="sec" style={{ fontSize: 10.5, color: "var(--neutral-400)" }}>{w.subWoCount} alt iş emri</span> : null}</td>
                        <td>{w.producedName}<span className="sec mono" style={{ fontSize: 10.5 }}>{w.producedCode}</span></td>
                        <td>{w.customerName ? (<>{w.customerName}{w.sourceOrderCode ? <span className="sec mono" style={{ fontSize: 10.5 }}>{w.sourceOrderCode}</span> : null}</>) : (<span style={{ color: "var(--neutral-400)" }}>Stoğa üretim</span>)}</td>
                        <td className="cell-num">{fmtQty(w.plannedQty)}</td>
                        <td className="cell-num"><span style={{ color: w.producedQty > 0 ? "var(--success-text)" : "var(--neutral-400)" }}>{fmtQty(w.producedQty)}</span></td>
                        <td className="cell-num"><span style={{ fontWeight: 600, color: complete ? "var(--success-text)" : "var(--warning-text)" }}>{fmtQty(remaining)}</span></td>
                        <td className="cell-num">
                          {w.batchSize && w.batchSize > 0 ? (
                            <span title={`Kalan ${fmtQty(remaining)} ÷ parti boyu ${fmtQty(w.batchSize)}${w.batchUom ? " " + w.batchUom : ""}`}>
                              {fmtQty(remaining / w.batchSize)}<span style={{ color: "var(--neutral-400)", fontSize: 10, marginLeft: 2 }}>parti</span>
                            </span>
                          ) : <span style={{ color: "var(--neutral-300)" }}>—</span>}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button type="button" className="btn-md primary" style={{ height: 30, padding: "0 11px", fontSize: 12 }} onClick={() => openWoBatch(w)}>
                              <Icon name="plus" size={13} />Üretim
                            </button>
                            {selectedMeta?.allowStockProduction ? (
                              <button type="button" className="btn-md warn" style={{ height: 30, padding: "0 11px", fontSize: 12 }} onClick={() => setLinkTarget({ woId: w.id, woCode: w.code, itemLabel: w.producedName, plannedQty: w.plannedQty, producedQty: w.producedQty })} title="Daha önce stoğa üretilmiş partileri bu iş emrine bağla">
                                <Icon name="boxes" size={13} />Stoktan Kullan
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ALT: Parti üretimleri */}
      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <b style={{ fontSize: 13 }}>Parti Üretimleri{centerName ? ` — ${centerName}` : ""}</b>
          <span className="sec" style={{ color: "var(--neutral-500)", fontSize: 12 }}>{batchTotal} kayıt</span>
          <div className="dt2-search-box" style={{ marginLeft: 10 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Parti, müşteri, ürün, lot, hat ara…"
              value={batchSearch}
              onChange={(e) => { setBatchSearch(e.target.value); setPage(1); }}
              aria-label="Parti üretimlerinde ara"
            />
          </div>
          <span className="grow" />
          <button
            type="button"
            className="btn-md ghost"
            style={{ height: 30, padding: "0 11px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, ...(filterCount > 0 ? { color: "var(--primary)", borderColor: "var(--primary)" } : {}) }}
            onClick={() => setFilterOpen((o) => !o)}
            aria-expanded={filterOpen}
            title="Ürün, tarih aralığı, vardiya ve kullanıcıya göre filtrele"
          >
            <Icon name="filter" size={14} />Filtrele
            {filterCount > 0 ? <span style={{ minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8, background: "var(--primary)", color: "#fff", fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{filterCount}</span> : null}
          </button>
          <Link
            to={selectedCenter ? `/production/parametre-raporu?merkez=${selectedCenter}` : "/production/parametre-raporu"}
            className="btn-md ghost"
            style={{ height: 30, padding: "0 11px", fontSize: 12, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            <Icon name="fileText" size={14} />Parametre Raporu
          </Link>
        </div>

        {/* Detaylı filtre çubuğu (masaüstü) — referans deseni: SearchSelect + tarih aralığı. Mobilde Sheet ile. */}
        {filterOpen ? (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14, padding: "11px 14px", borderBottom: "1px solid var(--neutral-200)", background: "var(--white)", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1.6 1 210px", minWidth: 190 }}>
              <span style={fLabel}>Ürün</span>
              <SearchSelect value={fProduct || null} options={productOptions} onChange={(v) => setFilter(() => setFProduct(v ?? ""))} placeholder="Tüm ürünler" searchPlaceholder="Ürün adı / kodu ara…" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "2 1 320px", minWidth: 300 }}>
              <span style={fLabel}>Tarih Aralığı</span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="date" className="form-input" style={{ height: 34, flex: "1 1 0", minWidth: 130 }} value={fSince} max={fUntil || undefined} onChange={(e) => setFilter(() => setFSince(e.target.value))} aria-label="Başlangıç" />
                <span style={{ color: "var(--neutral-400)" }}>–</span>
                <input type="date" className="form-input" style={{ height: 34, flex: "1 1 0", minWidth: 130 }} value={fUntil} min={fSince || undefined} onChange={(e) => setFilter(() => setFUntil(e.target.value))} aria-label="Bitiş" />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 160px", minWidth: 150 }}>
              <span style={fLabel}>Vardiya</span>
              <SearchSelect value={fShift || null} options={shiftOptions} onChange={(v) => setFilter(() => setFShift(v ?? ""))} placeholder="Tüm vardiyalar" searchPlaceholder="Vardiya ara…" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1.2 1 175px", minWidth: 160 }}>
              <span style={fLabel}>Kullanıcı</span>
              <SearchSelect value={fUser || null} options={userOptions} onChange={(v) => setFilter(() => setFUser(v ?? ""))} placeholder="Tüm kullanıcılar" searchPlaceholder="Kullanıcı ara…" />
            </div>
            {filterCount > 0 ? (
              <button type="button" className="btn-md ghost" style={{ height: 34, alignSelf: "flex-end", flexShrink: 0, color: "var(--neutral-600)" }} onClick={clearFilters}>
                <Icon name="x" size={14} />Temizle
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="dt2-scroll">
          {batchesQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : batchesQ.isError ? (
            <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{batchesQ.error?.message}</p></div>
          ) : batches.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">{batchQDebounced || filterCount > 0 ? "Filtreye uygun parti bulunamadı" : "Henüz parti üretim yok"}</p>
              <p className="sub">{batchQDebounced || filterCount > 0 ? "Arama terimini veya filtreleri değiştirin." : 'Yukarıdaki bir siparişin kaleminden "+Üretim" ile ilk partiyi kaydedin.'}</p>
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 168 }}>Parti Kodu</th>
                  <th style={{ width: 130 }}>Sipariş</th>
                  <th>Ürün</th>
                  <th className="num" style={{ width: 140 }}>Miktar</th>
                  <th style={{ width: 150 }}>Üretim Hattı</th>
                  {centerParams.map((p) => (
                    <th key={p.id} className={p.dataType === "number" ? "num" : undefined} style={{ width: 96 }} title={p.unit ? `${p.label} (${p.unit})` : p.label}>
                      {p.label}{p.unit ? <span style={{ color: "var(--neutral-400)", fontWeight: 400 }}> {p.unit}</span> : null}
                    </th>
                  ))}
                  <th style={{ width: 190 }}>Lot</th>
                  <th style={{ width: 140 }}>Tarih</th>
                  <th style={{ width: 84 }}>Yazdır</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const lb = b.lotStatus ? LOT_BADGE[b.lotStatus] ?? { cls: "", label: b.lotStatus } : null;
                  const pvals = new Map(b.params.map((p) => [p.paramId, p.value]));
                  return (
                    <tr key={b.id}>
                      <td><span className="cell-id">{b.code}</span></td>
                      <td><span className="mono" style={{ fontSize: 11.5 }}>{b.orderCode}</span></td>
                      <td>
                        {b.itemName}
                        <span className="sec mono" style={{ fontSize: 10.5 }}>{b.itemCode}</span>
                        {b.parentCode ? <span className="badge-pill b-info" style={{ marginLeft: 6, fontSize: 10 }} title={`Yan ürün — ana parti: ${b.parentCode}`}>Yan ürün · {b.parentCode}</span> : null}
                      </td>
                      <td className="cell-num">
                        {b.lotQtyOnHand != null && b.lotQtyOnHand < b.qty ? (
                          <span title={`Üretilen ${fmtQty(b.qty)} / Kalan ${fmtQty(b.lotQtyOnHand)} ${b.uom} (kısmi tüketim)`}>
                            {fmtQty(b.qty)}
                            <span style={{ color: "var(--neutral-400)" }}> / </span>
                            <span style={{ color: "var(--warning-text)", fontWeight: 600 }}>{fmtQty(b.lotQtyOnHand)}</span>
                            {" "}<span style={{ color: "var(--neutral-400)" }}>{b.uom}</span>
                          </span>
                        ) : (
                          <>{fmtQty(b.qty)} <span style={{ color: "var(--neutral-400)" }}>{b.uom}</span></>
                        )}
                      </td>
                      <td><span style={{ fontSize: 12 }}>{b.locationName}</span><span className="sec mono" style={{ fontSize: 10.5 }}>{b.locationCode}</span></td>
                      {centerParams.map((p) => {
                        const v = pvals.get(p.id);
                        return (
                          <td key={p.id} className={p.dataType === "number" ? "cell-num" : undefined}>
                            {v != null && v !== "" ? (
                              p.dataType === "boolean"
                                ? (v === "true" ? "Evet" : "Hayır")
                                : <span className={p.dataType === "number" ? "mono" : undefined} style={{ fontSize: 12 }}>{v}</span>
                            ) : <span style={{ color: "var(--neutral-300)" }}>—</span>}
                          </td>
                        );
                      })}
                      <td>
                        {b.lotCode ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span className="mono" style={{ fontSize: 11.5 }}>{b.lotCode}</span>
                            {lb ? <span className={`badge-pill ${lb.cls}`} style={{ alignSelf: "flex-start" }}>{lb.label}</span> : null}
                          </div>
                        ) : <span style={{ color: "var(--neutral-400)" }}>—</span>}
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: 11, color: "var(--neutral-600)" }}>{fmtDateTime(b.producedAt)}</span>
                        {b.shiftName ? <span className="sec" style={{ fontSize: 10.5, display: "block" }}>{b.shiftName}</span> : null}
                      </td>
                      <td>
                        <div className="row-acts">
                          <button type="button" className="iact info" aria-label="Tüketim detayı" title="Tüketim detayı — FEFO lot kırılımı" disabled={!b.lotId} onClick={() => setTraceTarget({ lotId: b.lotId, code: b.code })}>
                            <Icon name="list2" size={15} />
                          </button>
                          <button type="button" className="iact" aria-label="Parti etiketi yazdır" title="Parti etiketi yazdır (A4)" onClick={() => handlePrint(b)}>
                            <Icon name="printer" size={15} />
                          </button>
                          <button type="button" className="iact danger" aria-label="Partiyi sil" title="Partiyi sil — stok düşümleri geri alınır" onClick={() => { delMut.reset(); setDelBatch({ id: b.id, code: b.code }); }}>
                            <Icon name="trash" size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div><b>{batchTotal}</b> parti · <b>{batches.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + batches.length}</b> arası</div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      {drawers}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  MOBİL ÜRETİM GÖRÜNÜMÜ (≤767px) — GENTAŞ Saha referansı
//  Aynı tRPC verisi + aynı aksiyon drawer'ları; yalnız SUNUM tablo→kart olur.
// ═══════════════════════════════════════════════════════════════════════════

const KPI_LABEL: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--neutral-400)" };

function todayProducedStr(d: TodayStats): { main: string; sub: string } {
  if (!d) return { main: "—", sub: "" };
  if (d.count === 0) return { main: "0 parti", sub: "" };
  const parts = d.byUom.map((u) => `${fmtQty(u.qty)} ${u.uom}`);
  const shifts = (d.byShift ?? []).filter((s) => s.name !== "Vardiya dışı");
  const sub = shifts.length ? (d.byShift ?? []).map((s) => `${s.name}: ${s.count}`).join(" · ") : "";
  return { main: `${d.count} parti${parts.length ? " · " + parts.join(" · ") : ""}`, sub };
}

interface MobileProductionProps {
  centers: CenterLite[];
  selectedCenter: string | null;
  selectTab: (id: string | null) => void;
  hasCenterScope: boolean;
  selectedMeta: CenterMetaLite;
  centerName: string;
  todayStats: TodayStats;
  orders: InProdOrder[];
  ordersLoading: boolean;
  ordersError: string | null;
  expanded: Set<string>;
  toggle: (id: string) => void;
  openBatch: (o: InProdOrder, l: OrderLine) => void;
  queue: QueueRow[];
  queueLoading: boolean;
  openWoBatch: (w: QueueRow) => void;
  onWoDetail: (id: string) => void;
  onStockLink: (w: QueueRow) => void;
  onStockProduce: () => void;
  allowStock: boolean;
  batches: BatchRow[];
  batchesLoading: boolean;
  batchesError: string | null;
  batchTotal: number;
  page: number;
  totalPages: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onPrint: (b: BatchRow) => void;
  onTrace: (b: BatchRow) => void;
  onDelete: (b: BatchRow) => void;
  onFault: () => void;
  onLine: () => void;
  onAttendance: () => void;
  openReport: () => void;
  refreshAll: () => void;
  anyFetching: boolean;
  batchSearch: string;
  onBatchSearch: (v: string) => void;
  onFilter: () => void;
  filterCount: number;
}

function MobileProduction(p: MobileProductionProps) {
  const {
    centers, selectedCenter, selectTab, hasCenterScope, selectedMeta, centerName, todayStats,
    orders, ordersLoading, ordersError, expanded, toggle, openBatch,
    queue, queueLoading, openWoBatch, onWoDetail, onStockLink, onStockProduce, allowStock,
    batches, batchesLoading, batchesError, batchTotal, page, totalPages, setPage,
    onPrint, onTrace, onDelete, onFault, onLine, onAttendance, openReport, refreshAll, anyFetching,
    batchSearch, onBatchSearch, onFilter, filterCount,
  } = p;
  const today = todayProducedStr(todayStats);

  return (
    <>
      {/* Üst araç çubuğu — yenile + rapor + devam beyanı */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="btn-md secondary sm" style={{ flex: 1 }} onClick={refreshAll} disabled={anyFetching}>
          <Icon name="refresh" size={14} />{anyFetching ? "Yükleniyor…" : "Yenile"}
        </button>
        <button type="button" className="btn-md primary sm" style={{ flex: 1 }} onClick={openReport}>
          <Icon name="fileText" size={14} />Üretim Raporu
        </button>
        <button type="button" className="btn-md secondary sm" style={{ flex: "1 1 100%", justifyContent: "center" }} onClick={onAttendance}>
          <Icon name="users" size={14} />Personel Devam Beyanı
        </button>
      </div>

      {/* Üretim merkezi filtre şeridi — kaydırırken üstte sabit kalır */}
      {centers.length > 0 ? (
        <div className="m-stickytop">
          <Chips>
            {!hasCenterScope ? (
              <button type="button" className={`m-chip ${selectedCenter === null ? "act" : ""}`} onClick={() => selectTab(null)}>Tümü</button>
            ) : null}
            {centers.map((c) => (
              <button key={c.id} type="button" className={`m-chip ${selectedCenter === c.id ? "act" : ""}`} onClick={() => selectTab(c.id)}>{c.name}</button>
            ))}
          </Chips>
        </div>
      ) : (
        <div className="m-empty">Üretim merkezi tanımlı değil — Lokasyonlar sayfasından ekleyin.</div>
      )}

      {/* KPI kartı (yalnız merkez seçiliyken) */}
      {selectedCenter !== null ? (
        <MCard pad>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {([
              ["Günlük Kapasite", selectedMeta?.dailyCapacity != null ? `${fmtQty(Number(selectedMeta.dailyCapacity))}${selectedMeta.capacityUnit ? ` ${selectedMeta.capacityUnit}` : ""}` : "—"],
              ["Vardiya", selectedMeta?.shiftsPerDay != null ? `${selectedMeta.shiftsPerDay} × ${selectedMeta.hoursPerShift != null ? Number(selectedMeta.hoursPerShift) : "?"} sa` : "—"],
              ["Performans (OEE)", selectedMeta?.performancePct != null ? `%${fmtQty(Number(selectedMeta.performancePct))}` : "—"],
              ["Eş Zamanlı İş Emri", selectedMeta?.maxConcurrentWo != null ? String(selectedMeta.maxConcurrentWo) : "—"],
            ] as Array<[string, string]>).map(([l, v]) => (
              <div key={l}>
                <div style={KPI_LABEL}>{l}</div>
                <div className="mono" style={{ fontSize: 15, fontWeight: 700, color: "var(--neutral-900)", marginTop: 3 }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--neutral-100)" }}>
            <div style={KPI_LABEL}>Bugün Üretilen</div>
            <div className="mono" style={{ fontSize: 17, fontWeight: 700, color: "var(--neutral-900)", marginTop: 3 }}>{today.main}</div>
            {today.sub ? <div className="mono" style={{ fontSize: 12, color: "var(--neutral-500)", marginTop: 2 }}>{today.sub}</div> : null}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button type="button" className="btn-md secondary" style={{ flex: 1, color: "var(--danger)" }} onClick={onFault}><Icon name="alertTri" size={15} />Arıza Bildirimi</button>
            <button type="button" className="btn-md secondary" style={{ flex: 1 }} onClick={onLine}><Icon name="activity" size={15} />Hat Yönetimi</button>
          </div>
        </MCard>
      ) : null}

      {/* İş emri kuyruğu (merkez) / Üretimdeki siparişler (Tümü) */}
      {selectedCenter !== null ? (
        <>
          <div className="m-sec" style={{ display: "flex", alignItems: "center" }}>
            İş Emri Kuyruğu ({queue.length} açık)
            {allowStock ? (
              <button type="button" className="btn-md primary sm" style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0 }} onClick={onStockProduce}><Icon name="plus" size={14} />Stoğa Üretim</button>
            ) : null}
          </div>
          {queueLoading ? (
            <div className="m-empty">Yükleniyor…</div>
          ) : queue.length === 0 ? (
            <div className="m-empty">Bu merkezde açık iş emri yok</div>
          ) : (
            queue.map((w) => {
              const remaining = Math.max(0, w.plannedQty - w.producedQty);
              return (
                <MCard pad key={w.id}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button type="button" className="m-wo-code" onClick={() => onWoDetail(w.id)}>{w.code}</button>
                    {w.subWoCount > 0 ? <span style={{ fontSize: 11, color: "var(--neutral-400)" }}>{w.subWoCount} alt iş emri</span> : null}
                    <span className="badge b-neutral no-dot" style={{ marginLeft: "auto" }}>{centerName}</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--neutral-900)", margin: "8px 0 1px" }}>{w.producedName}</div>
                  <div className="mono" style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>{w.producedCode}</div>
                  {w.customerName ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--neutral-600)", marginTop: 6 }}>
                      <Icon name="users" size={13} />{w.customerName}{w.sourceOrderCode ? <span className="mono" style={{ color: "var(--neutral-400)", fontSize: 11.5 }}>{w.sourceOrderCode}</span> : null}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12.5, color: "var(--neutral-400)", marginTop: 6 }}>Stoğa üretim</div>
                  )}
                  <div style={{ display: "flex", gap: 12, margin: "12px 0", paddingTop: 10, borderTop: "1px solid var(--neutral-100)" }}>
                    {([["Planlanan", fmtQty(w.plannedQty), "var(--neutral-900)"], ["Üretilen", fmtQty(w.producedQty), "var(--info)"], ["Kalan", fmtQty(remaining), "var(--warning)"], ["Kalan Parti", w.batchSize && w.batchSize > 0 ? fmtQty(remaining / w.batchSize) : "—", "var(--neutral-700)"]] as Array<[string, string, string]>).map(([l, v, c]) => (
                      <div key={l} style={{ flex: 1 }}>
                        <div style={KPI_LABEL}>{l}</div>
                        <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: c, marginTop: 2 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="btn-md primary" style={{ flex: 1 }} onClick={() => openWoBatch(w)}><Icon name="plus" size={15} />Üretim</button>
                    {allowStock ? (
                      <button type="button" className="btn-md warn" style={{ flex: 1 }} onClick={() => onStockLink(w)}><Icon name="boxes" size={15} />Stoktan Kullan</button>
                    ) : null}
                  </div>
                </MCard>
              );
            })
          )}
        </>
      ) : (
        <>
          <div className="m-sec">Üretimdeki Siparişler ({orders.length})</div>
          {ordersLoading ? (
            <div className="m-empty">Yükleniyor…</div>
          ) : ordersError ? (
            <div className="m-empty" style={{ color: "var(--danger)" }}>{ordersError}</div>
          ) : orders.length === 0 ? (
            <div className="m-empty">Üretimde sipariş yok</div>
          ) : (
            orders.map((o) => {
              const open = expanded.has(o.id);
              const totalLines = o.lines.length;
              const doneLines = o.lines.filter((l) => l.producedQty >= l.qty && l.qty > 0).length;
              const totalOrdered = o.lines.reduce((s, l) => s + l.qty, 0);
              const totalProduced = o.lines.reduce((s, l) => s + l.producedQty, 0);
              const totalRemaining = o.lines.reduce((s, l) => s + Math.max(0, l.qty - l.producedQty), 0);
              const units = new Set(o.lines.map((l) => l.uom));
              const headUom = units.size === 1 ? [...units][0] : "";
              const allDone = totalOrdered > 0 && totalRemaining <= 0;
              return (
                <MCard pad key={o.id}>
                  <button type="button" onClick={() => toggle(o.id)} aria-expanded={open}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: 0, padding: 0, cursor: "pointer", textAlign: "left" }}>
                    <Icon name={open ? "chevDown" : "chevRight"} size={16} />
                    <span className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)" }}>{o.code}</span>
                    {o.awaitingQc ? <span className="badge b-warning no-dot" style={{ marginLeft: "auto" }}>QC bekliyor</span> : null}
                    <span className={`badge ${doneLines === totalLines && totalLines > 0 ? "b-success" : "b-warning"} no-dot`} style={{ marginLeft: o.awaitingQc ? 6 : "auto" }}>{doneLines}/{totalLines} kalem</span>
                  </button>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--neutral-900)", margin: "8px 0 2px" }}>{o.customerName}</div>
                  <div style={{ display: "flex", gap: 12, margin: "8px 0" }}>
                    {([["Sipariş", `${fmtQty(totalOrdered)}${headUom ? ` ${headUom}` : ""}`, "var(--neutral-900)"], ["Üretilen", fmtQty(totalProduced), "var(--info)"], ["Kalan", fmtQty(totalRemaining), allDone ? "var(--success)" : "var(--warning)"]] as Array<[string, string, string]>).map(([l, v, c]) => (
                      <div key={l} style={{ flex: 1 }}>
                        <div style={KPI_LABEL}>{l}</div>
                        <div className="mono" style={{ fontSize: 13.5, fontWeight: 700, color: c, marginTop: 2 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>Teslim: <span className="mono">{fmtDate(o.teslimTarihi)}</span>{o.teslimTahmini ? <span title="Söz verilen teslim tarihi girilmemiş — istenen tarih gösteriliyor"> *</span> : null}</div>
                  {open ? (
                    <MCard className="m-list" style={{ marginTop: 10 }}>
                      {o.lines.map((l) => {
                        const remaining = Math.max(0, l.qty - l.producedQty);
                        const complete = l.qty > 0 && l.producedQty >= l.qty;
                        return (
                          <div className="m-li" key={l.id} style={{ padding: "11px 12px" }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: "block", fontSize: 13, fontWeight: 600 }}>{l.itemName}</span>
                                <span className="mono" style={{ display: "block", fontSize: 10.5, color: "var(--neutral-400)" }}>{l.itemCode}</span>
                              </span>
                              {l.itemKind === "stock" ? (
                                <span style={{ fontSize: 11, color: "var(--neutral-400)", flexShrink: 0 }}>stok kalemi</span>
                              ) : (
                                <button type="button" className="btn-md primary sm" style={{ flexShrink: 0 }} onClick={() => openBatch(o, l)}><Icon name="plus" size={13} />Üretim</button>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                              {([["Sipariş", `${fmtQty(l.qty)} ${l.uom}`, "var(--neutral-700)"], ["Üretilen", fmtQty(l.producedQty), "var(--info)"], ["Kalan", complete ? "0" : `${fmtQty(remaining)} ${l.uom}`, complete ? "var(--success)" : "var(--warning)"]] as Array<[string, string, string]>).map(([lb, v, c]) => (
                                <div key={lb} style={{ flex: 1 }}>
                                  <div style={KPI_LABEL}>{lb}</div>
                                  <div className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: c, marginTop: 2 }}>{v}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </MCard>
                  ) : null}
                </MCard>
              );
            })
          )}
        </>
      )}

      {/* Parti üretimleri */}
      <div className="m-sec" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span>Parti Üretimleri{centerName ? ` — ${centerName}` : ""} ({batchTotal})</span>
        <button
          type="button"
          className="btn-md ghost sm"
          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, ...(filterCount > 0 ? { color: "var(--primary)" } : {}) }}
          onClick={onFilter}
        >
          <Icon name="filter" size={14} />Filtre{filterCount > 0 ? ` (${filterCount})` : ""}
        </button>
        <Link
          to={selectedCenter ? `/production/parametre-raporu?merkez=${selectedCenter}` : "/production/parametre-raporu"}
          className="btn-md ghost sm"
          style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}
        >
          <Icon name="fileText" size={14} />Rapor
        </Link>
      </div>
      <input
        className="m-search"
        type="search"
        placeholder="Parti, müşteri, ürün, lot ara…"
        value={batchSearch}
        onChange={(e) => onBatchSearch(e.target.value)}
        aria-label="Parti üretimlerinde ara"
      />
      {batchesLoading ? (
        <div className="m-empty">Yükleniyor…</div>
      ) : batchesError ? (
        <div className="m-empty" style={{ color: "var(--danger)" }}>{batchesError}</div>
      ) : batches.length === 0 ? (
        <div className="m-empty">{batchSearch.trim() || filterCount > 0 ? "Filtreye uygun parti bulunamadı" : "Henüz parti üretim yok"}</div>
      ) : (
        <MCard className="m-list">
          {batches.map((b) => {
            const lb = b.lotStatus ? LOT_BADGE[b.lotStatus] ?? { cls: "", label: b.lotStatus } : null;
            const badgeCls = lb ? (lb.cls === "b-warn" ? "b-warning" : lb.cls || "b-neutral") : "b-neutral";
            return (
              <div className="m-li" key={b.id} style={{ padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)" }}>{b.code}</span>
                  <span className="mono" style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 700 }}>{fmtQty(b.qty)} {b.uom}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginTop: 5 }}>
                  {b.itemName}
                  {b.parentCode ? <span className="badge b-info no-dot" style={{ marginLeft: 6, fontSize: 10 }}>Yan · {b.parentCode}</span> : null}
                </div>
                <div className="mono" style={{ fontSize: 11, color: "var(--neutral-400)", marginTop: 1 }}>{b.itemCode} · {b.orderCode}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  <span className="badge b-primary no-dot">{b.locationName}</span>
                  {b.lotCode ? <span className="badge b-neutral no-dot mono">{b.lotCode}</span> : null}
                  {lb ? <span className={`badge ${badgeCls} no-dot`}>{lb.label}</span> : null}
                  <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--neutral-400)" }}>{fmtDateTime(b.producedAt)}{b.shiftName ? ` · ${b.shiftName}` : ""}</span>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Tüketim detayı" disabled={!b.lotId} onClick={() => onTrace(b)}><Icon name="list2" size={16} /></button>
                  <button type="button" className="m-iconbtn m-li-act" aria-label="Etiket yazdır" onClick={() => onPrint(b)}><Icon name="printer" size={16} /></button>
                  <button type="button" className="m-iconbtn m-li-act danger" aria-label="Partiyi sil" onClick={() => onDelete(b)}><Icon name="trash" size={16} /></button>
                </div>
              </div>
            );
          })}
        </MCard>
      )}
      {totalPages > 1 ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
          <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((n) => Math.max(1, n - 1))}>‹ Önceki</button>
          <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
          <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((n) => Math.min(totalPages, n + 1))}>Sonraki ›</button>
        </div>
      ) : null}
    </>
  );
}
